import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type {
  TournamentDraft,
  TournamentDraftSlices,
  PersistedWizardState,
  FixtureConfigSlice,
  TeamConfigSlice,
  TournamentConfigSlice,
} from '../engine/types';
import { CATEGORY_RANKING_TARGET } from '@/types/tournament.model';
import type { Category } from '@/types/tournament.model';

// ============================================================================
// CONSTANTS
// ============================================================================

const STORAGE_KEY = 'tournament-wizard-draft';
const INITIAL_FLOW_ID = 'sport-selection';
const INITIAL_STEP_ID = 'sport';

// ============================================================================
// INITIAL STATE
// ============================================================================

/**
 * Creates a new empty draft with default values.
 */
export function createEmptyDraft(): TournamentDraft {
  const now = new Date().toISOString();

  return {
    // Metadata
    id: generateDraftId(),
    createdAt: now,
    updatedAt: now,
    currentStepId: INITIAL_STEP_ID,
    flowId: INITIAL_FLOW_ID,
    completedSteps: [],

    // Data slices - all null initially
    sport: null,
    basicInfo: null,
    teamConfig: null,
    fixtureConfig: null,
    pricing: null,
    clubSelection: null,
    schedule: null,
    scheduleSelection: null,
    tournamentConfig: null,
    clubSchedules: null,
  };
}

/**
 * Generates a unique draft ID.
 * Format: random 8-character alphanumeric string.
 */
function generateDraftId(): string {
  return Math.random().toString(36).substring(2, 10);
}

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Safely converts a persisted value (string/Date/null) into a Date or null.
 * Used to normalize date fields after rehydrating the draft from localStorage.
 */
function toDateOrNull(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string') {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

/**
 * Normalizes date-like fields in the tournament draft after persistence.
 * This keeps component code simpler and avoids calling Date methods on strings.
 */
type LegacyFixtureConfig = FixtureConfigSlice & {
  minCategory?: Category;
  maxCategory?: Category;
  forceSum?: boolean;
};

type LegacyTeamConfig = TeamConfigSlice & {
  minTeamsAllowed?: number;
  maxTeamsAllowed?: number;
};

/**
 * Moves fields between draft slices for persisted data shaped like the pre–v3 wizard
 * (categorías en fixture, cupos de equipos en team).
 */
function migrateDraftSlicesLayout(draft: TournamentDraft): TournamentDraft {
  let fixtureConfig = draft.fixtureConfig as LegacyFixtureConfig | null;
  let teamConfig = draft.teamConfig as LegacyTeamConfig | null;

  if (
    fixtureConfig &&
    fixtureConfig.minCategory != null &&
    fixtureConfig.maxCategory != null &&
    teamConfig
  ) {
    const fixtureConfigWithLegacyCategories = fixtureConfig;
    const hasTeamCategories =
      teamConfig.minCategory != null && teamConfig.maxCategory != null;
    if (!hasTeamCategories) {
      teamConfig = {
        ...teamConfig,
        minCategory: fixtureConfigWithLegacyCategories.minCategory!,
        maxCategory: fixtureConfigWithLegacyCategories.maxCategory!,
        forceSum: fixtureConfigWithLegacyCategories.forceSum ?? false,
      };
    }
    const fcRest: LegacyFixtureConfig = { ...fixtureConfigWithLegacyCategories };
    delete fcRest.minCategory;
    delete fcRest.maxCategory;
    delete fcRest.forceSum;
    fixtureConfig = fcRest as FixtureConfigSlice;
  }

  const legacyTeamsOnTeam =
    teamConfig &&
    typeof teamConfig.minTeamsAllowed === 'number' &&
    typeof teamConfig.maxTeamsAllowed === 'number';

  if (legacyTeamsOnTeam && fixtureConfig) {
    const fcNeedsTeams =
      fixtureConfig.minTeamsAllowed === undefined ||
      fixtureConfig.maxTeamsAllowed === undefined;
    if (fcNeedsTeams) {
      fixtureConfig = {
        ...fixtureConfig,
        minTeamsAllowed: teamConfig!.minTeamsAllowed!,
        maxTeamsAllowed: teamConfig!.maxTeamsAllowed!,
      };
      const tcRest: LegacyTeamConfig = { ...teamConfig! };
      delete tcRest.minTeamsAllowed;
      delete tcRest.maxTeamsAllowed;
      teamConfig = tcRest as TeamConfigSlice;
    }
  }

  return {
    ...draft,
    fixtureConfig,
    teamConfig,
  };
}

type LegacyTournamentConfig = Omit<
  TournamentConfigSlice,
  | 'countsForCategoryRanking'
  | 'categoryRankingTarget'
  | 'categoryRankingCategory'
> &
  Partial<TournamentConfigSlice>;

/**
 * Fills the category-ranking fields on drafts persisted before they existed. The step
 * schema requires them, so a draft saved by the previous version would fail validation
 * and silently block the "Siguiente" button.
 */
function migrateDraftCategoryRanking(draft: TournamentDraft): TournamentDraft {
  const tournamentConfig =
    draft.tournamentConfig as LegacyTournamentConfig | null;
  if (!tournamentConfig) return draft;

  return {
    ...draft,
    tournamentConfig: {
      ...tournamentConfig,
      countsForCategoryRanking: tournamentConfig.countsForCategoryRanking ?? true,
      categoryRankingTarget:
        tournamentConfig.categoryRankingTarget ??
        CATEGORY_RANKING_TARGET.PLAYER_CATEGORY,
      categoryRankingCategory: tournamentConfig.categoryRankingCategory ?? null,
    },
  };
}

/**
 * The fixed target category of the ranking must stay inside the tournament's category
 * range, which is picked in an earlier step. Narrowing that range leaves the target
 * outside it, and the backend rejects the tournament on submit — so drop the target and
 * invalidate the step, forcing the user back through it to pick a valid one.
 */
function reconcileCategoryRankingCategory(
  draft: TournamentDraft,
): TournamentDraft {
  const tournamentConfig = draft.tournamentConfig;
  const target = tournamentConfig?.categoryRankingCategory;
  if (!tournamentConfig || !target) return draft;

  const min = draft.teamConfig?.minCategory?.minimumElo;
  const max = draft.teamConfig?.maxCategory?.minimumElo;
  if (min === undefined || max === undefined) return draft;

  const isInRange =
    target.minimumElo >= Math.min(min, max) &&
    target.minimumElo <= Math.max(min, max);
  if (isInRange) return draft;

  return {
    ...draft,
    tournamentConfig: {
      ...tournamentConfig,
      categoryRankingCategory: null,
      isValid: false,
    },
  };
}

function normalizeDraftDates(draft: TournamentDraft | null): TournamentDraft | null {
  if (!draft) return draft;

  const normalized: TournamentDraft = { ...draft };

  // Legacy schedule slice (single-club mode)
  if (normalized.schedule) {
    normalized.schedule = {
      ...normalized.schedule,
      startDate: toDateOrNull(normalized.schedule.startDate),
      endDate: toDateOrNull(normalized.schedule.endDate),
      signupDeadlineDate: toDateOrNull(normalized.schedule.signupDeadlineDate),
    };
  }

  // Legacy combined schedule selection slice
  if (normalized.scheduleSelection) {
    normalized.scheduleSelection = {
      ...normalized.scheduleSelection,
      signupDeadlineDate: toDateOrNull(
        normalized.scheduleSelection.signupDeadlineDate,
      ),
    };
  }

  // New tournament config slice
  if (normalized.tournamentConfig) {
    normalized.tournamentConfig = {
      ...normalized.tournamentConfig,
      signupDeadlineDate: toDateOrNull(
        normalized.tournamentConfig.signupDeadlineDate,
      ),
    };
  }

  return normalized;
}

// ============================================================================
// STORE INTERFACE
// ============================================================================

interface WizardStoreState {
  // Draft state
  draft: TournamentDraft | null;
  hasPendingDraft: boolean;

  // Actions - Draft lifecycle
  initializeDraft: () => TournamentDraft;
  loadExistingDraft: () => TournamentDraft | null;
  clearDraft: () => void;
  hasDraft: () => boolean;

  // Actions - Draft updates
  updateDraft: (updates: Partial<TournamentDraft>) => void;
  updateSlice: <K extends keyof TournamentDraftSlices>(
    key: K,
    data: Partial<NonNullable<TournamentDraftSlices[K]>>
  ) => void;
  setSlice: <K extends keyof TournamentDraftSlices>(
    key: K,
    data: TournamentDraftSlices[K]
  ) => void;

  // Actions - Step management
  setCurrentStep: (stepId: string) => void;
  markStepComplete: (stepId: string) => void;
  markStepIncomplete: (stepId: string) => void;
  isStepCompleted: (stepId: string) => boolean;

  // Actions - Flow management
  setFlowId: (flowId: string) => void;

  // Internal
  _touchUpdatedAt: () => void;
}

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

export const useWizardStore = create<WizardStoreState>()(
  persist(
    (set, get) => ({
      // ========================================
      // Initial state
      // ========================================
      draft: null,
      hasPendingDraft: false,

      // ========================================
      // Draft lifecycle
      // ========================================

      initializeDraft: () => {
        const newDraft = createEmptyDraft();
        set({
          draft: newDraft,
          hasPendingDraft: false,
        });
        return newDraft;
      },

      loadExistingDraft: () => {
        const { draft } = get();
        if (draft) {
          set({ hasPendingDraft: false });
        }
        return draft;
      },

      clearDraft: () => {
        set({
          draft: null,
          hasPendingDraft: false,
        });
      },

      hasDraft: () => {
        return get().draft !== null;
      },

      // ========================================
      // Draft updates
      // ========================================

      updateDraft: (updates) => {
        const { draft, _touchUpdatedAt } = get();
        if (!draft) return;

        set({
          draft: {
            ...draft,
            ...updates,
            updatedAt: new Date().toISOString(),
          },
        });
        _touchUpdatedAt();
      },

      updateSlice: (key, data) => {
        const { draft } = get();
        if (!draft) return;

        const currentSlice = draft[key];
        const updatedSlice = currentSlice
          ? { ...currentSlice, ...data }
          : (data as NonNullable<TournamentDraftSlices[typeof key]>);

        const updatedDraft: TournamentDraft = {
          ...draft,
          [key]: updatedSlice,
          updatedAt: new Date().toISOString(),
        };

        set({
          draft:
            key === 'teamConfig'
              ? reconcileCategoryRankingCategory(updatedDraft)
              : updatedDraft,
        });
      },

      setSlice: (key, data) => {
        const { draft } = get();
        if (!draft) return;

        const updatedDraft: TournamentDraft = {
          ...draft,
          [key]: data,
          updatedAt: new Date().toISOString(),
        };

        set({
          draft:
            key === 'teamConfig'
              ? reconcileCategoryRankingCategory(updatedDraft)
              : updatedDraft,
        });
      },

      // ========================================
      // Step management
      // ========================================

      setCurrentStep: (stepId) => {
        const { draft } = get();
        if (!draft) return;

        set({
          draft: {
            ...draft,
            currentStepId: stepId,
            updatedAt: new Date().toISOString(),
          },
        });
      },

      markStepComplete: (stepId) => {
        const { draft } = get();
        if (!draft) return;

        const completedSteps = draft.completedSteps.includes(stepId)
          ? draft.completedSteps
          : [...draft.completedSteps, stepId];

        set({
          draft: {
            ...draft,
            completedSteps,
            updatedAt: new Date().toISOString(),
          },
        });
      },

      markStepIncomplete: (stepId) => {
        const { draft } = get();
        if (!draft) return;

        set({
          draft: {
            ...draft,
            completedSteps: draft.completedSteps.filter((id) => id !== stepId),
            updatedAt: new Date().toISOString(),
          },
        });
      },

      isStepCompleted: (stepId) => {
        const { draft } = get();
        return draft?.completedSteps.includes(stepId) ?? false;
      },

      // ========================================
      // Flow management
      // ========================================

      setFlowId: (flowId) => {
        const { draft } = get();
        if (!draft) return;

        set({
          draft: {
            ...draft,
            flowId,
            updatedAt: new Date().toISOString(),
          },
        });
      },

      // ========================================
      // Internal helpers
      // ========================================

      _touchUpdatedAt: () => {
        // This is called after updates to ensure persistence triggers
        // The actual update is done in the individual methods
      },
    }),
    {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => localStorage),

      // Only persist the draft, not internal flags
      partialize: (state): PersistedWizardState => ({
        draft: state.draft,
        lastUpdated: state.draft?.updatedAt ?? null,
      }),

      // Rehydrate the store from localStorage
      onRehydrateStorage: () => (state) => {
        if (state?.draft) {
          state.hasPendingDraft = true;
          state.draft = migrateDraftSlicesLayout(state.draft);
        }
      },

      // Handle version migrations if needed
      version: 4,
      migrate: (persistedState, version) => {
        let state = persistedState as WizardStoreState;

        if (version < 2 && state?.draft) {
          state = {
            ...state,
            draft: normalizeDraftDates(state.draft),
          } as WizardStoreState;
        }

        if (version < 3 && state?.draft) {
          state = {
            ...state,
            draft: migrateDraftSlicesLayout(state.draft),
          } as WizardStoreState;
        }

        if (version < 4 && state?.draft) {
          state = {
            ...state,
            draft: migrateDraftCategoryRanking(state.draft),
          } as WizardStoreState;
        }

        return state;
      },
    }
  )
);

// ============================================================================
// SELECTORS
// ============================================================================

/**
 * Selector to get the current sport ID from the draft.
 */
export const selectSportId = (state: WizardStoreState): string | null => {
  return state.draft?.sport?.sport?.uid?.toLowerCase() ?? null;
};

/**
 * Selector to get the current step ID.
 */
export const selectCurrentStepId = (state: WizardStoreState): string | null => {
  return state.draft?.currentStepId ?? null;
};

/**
 * Selector to get the current flow ID.
 */
export const selectFlowId = (state: WizardStoreState): string | null => {
  return state.draft?.flowId ?? null;
};

/**
 * Selector to check if a specific slice has data.
 */
export const selectHasSliceData = (
  state: WizardStoreState,
  key: keyof TournamentDraftSlices
): boolean => {
  return state.draft?.[key] !== null;
};

// ============================================================================
// HOOKS HELPERS
// ============================================================================

/**
 * Hook to get a specific slice from the draft.
 */
export function useDraftSlice<K extends keyof TournamentDraftSlices>(
  key: K
): TournamentDraftSlices[K] {
  return useWizardStore((state) => state.draft?.[key] ?? null);
}

/**
 * Hook to get the draft's sport ID.
 */
export function useSportId(): string | null {
  return useWizardStore(selectSportId);
}

/**
 * Hook to get the current step ID.
 */
export function useCurrentStepId(): string | null {
  return useWizardStore(selectCurrentStepId);
}
